import { type ReactNode } from "react";
import { cn } from "@/lib/utils";

/** Rail links and buttons: 44px targets below 1024px, 24px above (ACCESSIBILITY.md § Target size). */
export const RAIL_TARGETS =
  "[&_a]:inline-flex [&_a]:min-h-11 [&_a]:min-w-11 [&_a]:items-center [&_button]:min-h-11 [&_button]:min-w-11 lg:[&_a]:min-h-6 lg:[&_a]:min-w-6 lg:[&_button]:min-h-6 lg:[&_button]:min-w-6";

export interface TemplateBodyProps {
  /** The primary page zone. */
  children: ReactNode;
  /** Optional related-records or secondary-content rail. */
  rail?: ReactNode;
  /** The rail landmark's name (`<aside aria-label>`, ACCESSIBILITY.md § Landmarks). */
  railLabel?: string;
  /** Visible heading over the stacked rail on phones (SURFACES.md § Responsive); hidden from 640px up, where the rail sits beside or below the main column. */
  railHeading?: string;
  className?: string;
  mainClassName?: string;
  railClassName?: string;
}

/**
 * Main content with an optional fixed rail. The rail is a full-width second
 * zone below `--layout-rail-breakpoint`, preserving every action on narrow screens.
 *
 * @example
 * <TemplateBody rail={<RelatedPeople />}>{table}</TemplateBody>
 */
export function TemplateBody(
  { children, rail, railLabel = "Related", railHeading = "About", className, mainClassName, railClassName }:
    TemplateBodyProps,
) {
  return (
    <div
      className={cn(
        "grid min-w-0 gap-[var(--density-page-gap)] min-[1280px]:grid-cols-[minmax(0,1fr)_var(--layout-rail-width)]",
        className,
      )}
    >
      <div className={cn("min-w-0", mainClassName)}>{children}</div>
      {rail
        ? (
          <aside aria-label={railLabel} className={cn("min-w-0", railClassName)}>
            <h2 className="mb-2 text-base font-semibold min-[640px]:hidden">{railHeading}</h2>
            {rail}
          </aside>
        )
        : null}
    </div>
  );
}
